
import $ from 'jquery';
import { Component, Vue } from 'vue-property-decorator';
import { handleError, initClient } from '../../../api';
import {
  ApprovalState,
  Group,
  GroupSurveyExtension,
  GroupSurveyExtensionUiClient,
  GroupSurveyMapping,
  GroupUserExternal,
  IGroup,
  IGroupSurveyExtension,
  IGroupSurveyMapping,
  IPresenceStartRequest,
  PresenceStartRequest,
  SettingsApiControllerUIClient,
  VisitorManagementUiClient,
} from '../../backend/backendapi';
import { blobDownload, emptyUUID, formatDateTime, WindowType } from '../../../util';
import Toasted from 'vue-toasted';
import GroupEditor from '../../group/EditModal/GroupEditor';
import TabSlot from '../../group/EditModal/TabSlot';
import GroupSurveyEditor from '../../survey/group/EditModal/GroupSurveyEditor';
import GeneralAfter from '../../survey/group/EditModal/GeneralAfter';
import Spinner from '../../util/Spinner';
import GroupSurveyGroupSelector from '../../visitorManagment/advanceRegistration/AdvanceRegistration/GroupSurveyGroupSelector.vue';

declare var window: WindowType;

Vue.use(Toasted, { duration: 2000 });

@Component({
  components: {
    GeneralAfter,
    GroupEditor,
    GroupSurveyEditor,
    GroupSurveyGroupSelector,
    Spinner,
    TabSlot,
  },
})
export default class DirectRegistrationModal extends Vue {
  private readonly api = initClient(GroupSurveyExtensionUiClient);
  private readonly settingsApi = initClient(SettingsApiControllerUIClient);
  private readonly visitorApi = initClient(VisitorManagementUiClient);

  canToggleEnableScanEntry = false;
  extraInfoJson: string = '[]';
  groupSurveyExtension: GroupSurveyExtension | null = null;
  surveySelectionViaGroups = false;
  generateBadges = false;
  done = false;
  saving = false;

  GroupSurveyExtension = GroupSurveyExtension;
  formatDateTime = formatDateTime;

  // Add state for change of selection of survey groups, so we can compute if at least one is selected
  selectedSurveyGroups: GroupSurveyExtension[] = [];

  get isSurveySelectionGroupsSelectedOrInactive(): boolean {
    if (!this.surveySelectionViaGroups) return true;
    return this.selectedSurveyGroups.length > 0;
  }

  get canSave(): boolean {
    if (!this.groupSurveyExtension?.group.name
        || !this.groupSurveyExtension.scanEntryStartDate
        || !this.groupSurveyExtension.scanEntryEndDate
        || !this.groupSurveyExtension.responsibleContactId
        || !this.groupSurveyExtension.group.externalUsers.length) {
      return false;
    }
    return this.isSurveySelectionGroupsSelectedOrInactive;
  }

  get uid(): unknown {
    return this._uid;
  }

  async mounted(): Promise<void> {
    await this.reloadSetting();
  }

  public open(): void {
    const startDate = new Date();
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
    endDate.setHours(endDate.getHours() + 7, 0, 0, 0);

    (this.$refs.groupSurveyGroupSelector as GroupSurveyGroupSelector)?.selectedGroups.splice(0);
    this.groupSurveyExtension = GroupSurveyExtension.fromJS({
      id: emptyUUID(),
      groupId: emptyUUID(),
      group: Group.fromJS({
        id: emptyUUID(),
        name: '',
        createdOn: new Date(),
        createdBy: window.application.user.id,
        feature: 'survey',
        archived: false,
        archivedOn: undefined,
        archivedBy: undefined,
        userIds: [],
        externalUsers: [],
        isPinned: false,
      } as IGroup),
      isAdvanceRegistration: true,
      surveyMappings: [],
      enableScanEntry: false,
      scanEntryEndDate: endDate,
      scanEntryStartDate: startDate,
      responsibleContactId: undefined,
      responsibleFallbackContactId: undefined,
      collectiveApprovalState: ApprovalState.NotRequired,
      isVisitorGroup: false,
      extraInfoJson: '[]',
      visitorGroupNames: [],
      userPresences: [],
      approvals: [],
      informationGroupMappings: [],
    } as IGroupSurveyExtension);

    if (!this.canToggleEnableScanEntry) {
      this.groupSurveyExtension.enableScanEntry = true;
    }
    this.groupSurveyExtension.extraInfoJson = this.extraInfoJson;

    if (this.surveySelectionViaGroups) {
      this.$nextTick(() => (this.$refs.groupEditor as GroupEditor<GroupSurveyExtension>).tab = 'surveysFromGroup');
    }

    $(this.$refs.modal!).modal('show');
  }

  public dismiss(): void {
    $(this.$refs.modal!).modal('hide');
  }

  addSurveysFromSelectedGroups(): void {
    if (!this.groupSurveyExtension) {
      return;
    }

    const existingSurveyIds = new Set(this.groupSurveyExtension.surveyMappings.map(m => m.surveyId));
    const groups: GroupSurveyExtension[] = (this.$refs.groupSurveyGroupSelector as GroupSurveyGroupSelector)?.selectedGroups ?? [];
    const surveyIds = [...new Set(groups.flatMap(g => g.surveyMappings.map(m => m.surveyId)))]
        .filter(id => !existingSurveyIds.has(id));
    const mappings = surveyIds.map(id => GroupSurveyMapping.fromJS({
      id: emptyUUID(),
      groupSurveyExtensionId: emptyUUID(),
      surveyId: id,
    } as IGroupSurveyMapping));
    this.groupSurveyExtension.surveyMappings.push(...mappings);

    this.groupSurveyExtension.visitorGroupNames = groups.map(e => e.group.name);
  }

  async onSave(andCheckIn = false): Promise<void> {
    if (!this.groupSurveyExtension) {
      return;
    }

    this.addSurveysFromSelectedGroups();

    //ensure start date is minimally in the future so saving works
    if (this.groupSurveyExtension.scanEntryStartDate && this.groupSurveyExtension.scanEntryStartDate.getTime() < Date.now() + 60000) {
      this.groupSurveyExtension.scanEntryStartDate = new Date(Date.now() + 60000);
    }

    this.saving = true;
    try {
      const res = await (this.$refs.groupEditor as GroupEditor<GroupSurveyExtension>).onSave();
      if (res) { //res = undefined if saving failed
        this.groupSurveyExtension = res;

        if (andCheckIn) {
          for (let user of this.groupSurveyExtension!.group.userIds) {
            await this.api.presenceStart(this.groupSurveyExtension!.id, PresenceStartRequest.fromJS({ userId: user.userId } as IPresenceStartRequest));
          }
          for (let externalUser of this.groupSurveyExtension!.group.externalUsers) {
            await this.api.presenceStart(this.groupSurveyExtension!.id, PresenceStartRequest.fromJS({ externalUserId: externalUser.id } as IPresenceStartRequest));
          }
        }

        if (this.generateBadges) {
          const badgePromises = this.groupSurveyExtension!.group.externalUsers.map(externalUser => this.printBadge(this.groupSurveyExtension!, externalUser));
          await Promise.allSettled(badgePromises);
        }

        //hide editor
        this.dismiss();
        this.$emit('saved');
      }
    } finally {
      this.saving = false;
    }
  }

  async printBadge(groupSurveyExtension: GroupSurveyExtension, groupUserExternal: GroupUserExternal): Promise<void> {
    try {
      const response = await this.visitorApi.printBadge(groupSurveyExtension.id, groupUserExternal.id);
      blobDownload(response.data, response.fileName ?? 'badge.pdf');
    } catch (err) {
      handleError(this.$toasted, this.$i18n, err);
    }
  }

  async reloadSetting(): Promise<void> {
    try {
      const allowToggleScanEntry = await this.settingsApi.getSystemSetting('VisitorManagementSettings.AdvanceRegistrationOnlyAllowToggleForEnableScanEntry');
      this.canToggleEnableScanEntry = (allowToggleScanEntry.result! as string).toLocaleLowerCase() === 'true';
    } catch (error) { //in case the setting does not exist
      console.error(error);
    }
    try {
      const surveySelectionViaGroups = await this.settingsApi.getSystemSetting('VisitorManagementSettings.EnableAdvanceRegistrationSurveySelectionViaGroups');
      this.surveySelectionViaGroups = (surveySelectionViaGroups.result! as string).toLocaleLowerCase() === 'true';
    } catch (error) { //in case the setting does not exist
      console.error(error);
    }
    try {
      const extraInfoJson = await this.settingsApi.getSystemSetting('VisitorManagementSettings.AdvanceRegistrationExtraInfoJson');
      if (Array.isArray(extraInfoJson.result!)) {
        this.extraInfoJson = JSON.stringify(extraInfoJson.result);
      } else {
        this.extraInfoJson = '[]';
      }
    } catch (error) { //in case the setting does not exist
      console.error(error);
      this.extraInfoJson = '[]';
    }
  }

  async saveGroup(group: GroupSurveyExtension): Promise<GroupSurveyExtension> {
    let res: GroupSurveyExtension;

    if (!group.id || group.id === emptyUUID()) {
      res = await this.api.create(group);
    } else {
      res = await this.api.update(group);
    }
    return res;
  }
}
